/**
 * Presence API — multi-user awareness
 *
 * Sends heartbeat every 30s while a user has a template open.
 * Server stores presence in KV; frontend polls active users.
 * Entries older than 90 seconds are considered stale.
 *
 * CRITICAL DESIGN DECISIONS:
 *
 * 1. NO AbortController: AbortController.abort() sends a TCP RST which causes
 *    the Deno server-side "Http: connection closed before message completed"
 *    error. Instead, uses Promise.race() with a soft timeout — the caller
 *    bails out but the HTTP connection finishes normally in the background.
 *
 * 2. ALL fetches use `keepalive: true`: When the user closes the browser tab,
 *    the browser terminates ALL non-keepalive in-flight requests by sending
 *    TCP RST to the server. Since presence heartbeats fire every 30s and
 *    polls every 15s, there's a ~2% chance one is in-flight during tab close.
 *    With `keepalive: true`, the browser lets the connection finish gracefully
 *    even during page unload, preventing the server "connection closed" error.
 *    Presence request bodies are tiny (<200 bytes), well under the browser's
 *    64KB cumulative keepalive limit.
 *
 * 3. Orphaned-promise guards: Both the fetch and timeout promises have
 *    `.catch(() => {})` so the "loser" of Promise.race doesn't create an
 *    unhandled rejection.
 */

import { API_BASE, getSessionTokenValue } from "./apiClient";
import { publicAnonKey } from "../../../utils/supabase/info";

/** Soft timeout for presence API calls (ms). */
const PRESENCE_TIMEOUT = 12_000;

export interface PresenceEntry {
  username: string;
  displayName: string;
  avatarUrl?: string;
  templateId: string;
  lastSeen: number;
}

/**
 * Fetch wrapper with soft timeout + keepalive.
 */
async function apiFetch(path: string, opts: RequestInit = {}) {
  let timeoutId: ReturnType<typeof setTimeout>;

  const sessionHeaders: Record<string, string> = {
    "Content-Type": "application/json",
    Authorization: `Bearer ${publicAnonKey}`,
    ...(opts.headers as Record<string, string> || {}),
  };
  const sessionToken = getSessionTokenValue();
  if (sessionToken) {
    sessionHeaders["X-Session-Token"] = sessionToken;
  }

  const fetchPromise = fetch(`${API_BASE}${path}`, {
    ...opts,
    keepalive: true, // Survive tab close — prevents server "connection closed" error
    headers: sessionHeaders,
  }).then(async (res) => {
    if (!res.ok) {
      const t = await res.text();
      throw new Error(`Presence API ${res.status}: ${t}`);
    }
    return res.json();
  });

  const timeoutPromise = new Promise<never>((_resolve, reject) => {
    timeoutId = setTimeout(() => reject(new Error("Presence API soft timeout")), PRESENCE_TIMEOUT);
  });

  // Attach no-op catch handlers so that the "losing" promise in the race
  // doesn't produce an unhandled rejection.
  fetchPromise.catch(() => {});
  timeoutPromise.catch(() => {});

  try {
    return await Promise.race([fetchPromise, timeoutPromise]);
  } finally {
    clearTimeout(timeoutId!);
  }
}

/** Send a heartbeat for the current user on a template */
export async function sendHeartbeat(
  templateId: string,
  username: string,
  displayName: string,
  avatarUrl?: string,
): Promise<void> {
  await apiFetch("/presence/heartbeat", {
    method: "POST",
    body: JSON.stringify({ templateId, username, displayName, avatarUrl }),
  });
}

/** Get all active users for a specific template */
export async function getPresence(
  templateId: string,
): Promise<PresenceEntry[]> {
  const data = await apiFetch(`/presence/${encodeURIComponent(templateId)}`);
  return data.users || [];
}

/** Get active users across all templates (for dashboard) */
export async function getAllPresence(): Promise<PresenceEntry[]> {
  const data = await apiFetch("/presence");
  return data.users || [];
}

/**
 * Remove presence when leaving a template.
 *
 * Synchronous fire-and-forget — callers (unmount cleanup functions)
 * cannot await async functions.
 *
 * Uses `keepalive: true` so the browser keeps the connection alive even
 * after the page/component navigates away.
 */
export function removePresence(
  templateId: string,
  username: string,
): void {
  try {
    const leaveHeaders: Record<string, string> = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${publicAnonKey}`,
    };
    const leaveToken = getSessionTokenValue();
    if (leaveToken) {
      leaveHeaders["X-Session-Token"] = leaveToken;
    }
    fetch(`${API_BASE}/presence/leave`, {
      method: "POST",
      body: JSON.stringify({ templateId, username }),
      keepalive: true,
      headers: leaveHeaders,
    }).catch(() => {
      // Silently ignore — this fires during unmount/navigation
    });
  } catch {
    // Silently ignore — synchronous errors during page teardown
  }
}